import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTheme } from 'common/styling/theme';
import { BaseSelectField, MoneyCell, PageContentPreloader, PageLayout } from 'common/components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { getTradingAccountList } from '../../_redux';
import {
  getAccountAnalytics,
  getAccountAnalyticsChartData,
  getAccountEquity,
  getUserAnalytics,
  getUserAnalyticsChartData,
  getUserEquity,
} from '../_redux';
import { staticStyles, getDynamicStyles } from './style';
import { ClientAnalyticsChart } from '../_components';

const ClientAnalyticsPage = ({
  getTradingAccountList,
  loadingAccounts,
  accountsIsLoaded,
  accounts,
  getAccountAnalytics,
  analytics,
  analyticsIsLoaded,
  loadingAnalytics,
  getAccountAnalyticsChartData,
  theme,
  getAccountEquity,
  accountEquity,
  accountEquityIsLoaded,
  loadingAccountEquity,
  userEquity,
  userEquityIsLoaded,
  loadingUserEquity,
  getUserAnalytics,
  getUserAnalyticsChartData,
  getUserEquity,
  userAnalytics,
  userAnalyticsIsLoaded,
  loadingUserAnalytics,
  userAnalyticsChartData,
  userAnalyticsChartDataIsLoaded,
  analyticsChartData,
  analyticsChartDataIsLoaded,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(7);
  const ALL_ACCOUNTS = 'all_accounts';

  const isAllAccounts = selectedAccountId === ALL_ACCOUNTS;

  const analyticsData = isAllAccounts ? userAnalytics : analytics;
  const analyticsDataIsLoaded = isAllAccounts ? userAnalyticsIsLoaded : analyticsIsLoaded;
  const loadingAnalyticsData = isAllAccounts ? loadingUserAnalytics : loadingAnalytics;

  const equityData = isAllAccounts ? userEquity : accountEquity;
  const equityDataIsLoaded = isAllAccounts ? userEquityIsLoaded : accountEquityIsLoaded;
  const loadingEquityData = isAllAccounts ? loadingUserEquity : loadingAccountEquity;

  const analyticsChart = isAllAccounts ? userAnalyticsChartData : analyticsChartData;
  const analyticsChartIsLoaded = isAllAccounts ? userAnalyticsChartDataIsLoaded : analyticsChartDataIsLoaded;

  const periodOptions = [
    { value: 7, label: '7 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
    { value: 365, label: '365 days' },
  ];

  useEffect(() => {
    getTradingAccountList({ noDetails: true, search: { analyticsEnabled: true } });
  }, [getTradingAccountList]);

  const accountOptions = useMemo(
    () => [
      {
        value: ALL_ACCOUNTS,
        label: 'All Accounts',
      },
      ...accounts.map(account => ({
        value: account.id,
        label: account.login,
      })),
    ],
    [accounts]
  );

  useEffect(() => {
    if (
      accountOptions.length &&
      (!selectedAccountId || !accountOptions.some(account => account.value === selectedAccountId))
    ) {
      setSelectedAccountId(accountOptions[0].value);
    }
  }, [accountOptions]);

  const loadAnalytics = () => {
    if (!selectedAccountId) {
      return;
    }

    const search = {
      period: selectedPeriod,
    };

    if (selectedAccountId === ALL_ACCOUNTS) {
      getUserAnalytics({ search });
      getUserAnalyticsChartData({ search });
      getUserEquity();
    } else {
      const payload = {
        id: selectedAccountId,
        search,
      };

      getAccountAnalytics(payload);
      getAccountAnalyticsChartData(payload);
      getAccountEquity({ id: selectedAccountId });
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [getAccountAnalytics, getAccountAnalyticsChartData, selectedAccountId, selectedPeriod]);

  const handleReload = () => {
    getTradingAccountList({ noDetails: true, search: { analyticsEnabled: true } });
    loadAnalytics();
  };

  return (
    <PageLayout
      captionSlug="justAnalytics"
      showReload
      reloading={loadingAccounts && loadingAnalyticsData && loadingEquityData}
      onReloadClick={handleReload}>
      <PageContentPreloader ready={accountsIsLoaded && analyticsDataIsLoaded && equityDataIsLoaded} type="bigLine">
        {accountsIsLoaded && analyticsDataIsLoaded && equityDataIsLoaded && (
          <div className="ClientAnalyticsPage">
            <div className="ClientAnalyticsPage__filters">
              <BaseSelectField
                name="account"
                textId="justAccount"
                value={accountOptions.find(item => item.value === selectedAccountId)}
                options={accountOptions}
                onChange={option => setSelectedAccountId(option?.value)}
              />

              <BaseSelectField
                name="period"
                textId="justPeriod"
                value={periodOptions.find(item => item.value === selectedPeriod)}
                options={periodOptions}
                onChange={option => setSelectedPeriod(option?.value)}
              />
            </div>

            <div className="ClientAnalyticsPage__analytics">
              <div className="ClientAnalyticsPage__analytics-item">
                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="netProfit">
                      {txt => <p className="ClientAnalyticsPage__analytics-item-metric-title-main">{txt}</p>}
                    </FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value-main">
                    <MoneyCell value={analyticsData.net_profit} />
                  </div>
                </div>

                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="justProfit">{txt => <p>{txt}</p>}</FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value">
                    <MoneyCell value={analyticsData.profit_sum} />
                  </div>
                </div>

                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="justLoss">{txt => <p>{txt}</p>}</FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value">
                    <MoneyCell value={analyticsData.loss_sum} />
                  </div>
                </div>
              </div>

              <div className="ClientAnalyticsPage__analytics-item">
                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="justClosedOrders">
                      {txt => <p className="ClientAnalyticsPage__analytics-item-metric-title-main">{txt}</p>}
                    </FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value-main">
                    <div> {analyticsData.closed_orders} </div>
                  </div>
                </div>

                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="justProfitable">{txt => <p>{txt}</p>}</FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value">
                    <div>{analyticsData.profitable_orders}</div>
                  </div>
                </div>

                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="justUnprofitable">{txt => <p>{txt}</p>}</FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value">
                    <div>{analyticsData.unprofitable_orders}</div>
                  </div>
                </div>
              </div>

              <div className="ClientAnalyticsPage__analytics-item">
                <div className="ClientAnalyticsPage__analytics-item-metric">
                  <div className="ClientAnalyticsPage__analytics-item-metric-title">
                    <FormattedMessage id="justEquity">
                      {txt => <p className="ClientAnalyticsPage__analytics-item-metric-title-main">{txt}</p>}
                    </FormattedMessage>
                  </div>
                  <div className="ClientAnalyticsPage__analytics-item-metric-value-main">
                    <MoneyCell value={equityData.equity} />
                  </div>
                </div>
              </div>
            </div>

            <ClientAnalyticsChart
              selectedPeriod={selectedPeriod}
              analyticsChartData={analyticsChart}
              analyticsChartDataIsLoaded={analyticsChartIsLoaded}
            />
          </div>
        )}
      </PageContentPreloader>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </PageLayout>
  );
};

ClientAnalyticsPage.propTypes = {
  getTradingAccountList: PropTypes.func.isRequired,
  loadingAccounts: PropTypes.bool.isRequired,
  accountsIsLoaded: PropTypes.bool.isRequired,
  accounts: PropTypes.array.isRequired,
  getAccountAnalytics: PropTypes.func.isRequired,
  analytics: PropTypes.object.isRequired,
  analyticsIsLoaded: PropTypes.bool.isRequired,
  loadingAnalytics: PropTypes.bool.isRequired,
  getAccountAnalyticsChartData: PropTypes.func.isRequired,
  theme: PropTypes.object,
  getAccountEquity: PropTypes.func.isRequired,
  accountEquity: PropTypes.object.isRequired,
  accountEquityIsLoaded: PropTypes.bool.isRequired,
  loadingAccountEquity: PropTypes.bool.isRequired,
  userEquity: PropTypes.object.isRequired,
  userEquityIsLoaded: PropTypes.bool.isRequired,
  loadingUserEquity: PropTypes.bool.isRequired,
  getUserAnalytics: PropTypes.func.isRequired,
  getUserAnalyticsChartData: PropTypes.func.isRequired,
  getUserEquity: PropTypes.func.isRequired,
  userAnalytics: PropTypes.object.isRequired,
  userAnalyticsIsLoaded: PropTypes.bool.isRequired,
  loadingUserAnalytics: PropTypes.bool.isRequired,
  userAnalyticsChartData: PropTypes.object.isRequired,
  userAnalyticsChartDataIsLoaded: PropTypes.bool.isRequired,
  analyticsChartData: PropTypes.object.isRequired,
  analyticsChartDataIsLoaded: PropTypes.bool.isRequired,
};

ClientAnalyticsPage.defaultProps = {
  theme: {},
};

export default compose(
  withTheme(),
  withRouter,
  connect(
    state => ({
      loadingAccounts: state.client.common.loadingAccounts,
      accountsIsLoaded: state.client.common.accountsIsLoaded,
      accounts: state.client.common.accounts,
      analytics: state.client.accounts.analytics,
      analyticsIsLoaded: state.client.accounts.analyticsIsLoaded,
      loadingAnalytics: state.client.accounts.loadingAnalytics,
      accountEquity: state.client.accounts.accountEquity,
      accountEquityIsLoaded: state.client.accounts.accountEquityIsLoaded,
      loadingAccountEquity: state.client.accounts.loadingAccountEquity,
      userEquity: state.client.accounts.userEquity,
      userEquityIsLoaded: state.client.accounts.userEquityIsLoaded,
      loadingUserEquity: state.client.accounts.loadingUserEquity,
      userAnalytics: state.client.accounts.userAnalytics,
      userAnalyticsIsLoaded: state.client.accounts.userAnalyticsIsLoaded,
      loadingUserAnalytics: state.client.accounts.loadingUserAnalytics,
      analyticsChartData: state.client.accounts.analyticsChartData,
      analyticsChartDataIsLoaded: state.client.accounts.analyticsChartDataIsLoaded,
      userAnalyticsChartData: state.client.accounts.userAnalyticsChartData,
      userAnalyticsChartDataIsLoaded: state.client.accounts.userAnalyticsChartDataIsLoaded,
    }),
    {
      getTradingAccountList: data => getTradingAccountList.request(data),
      getAccountAnalytics: data => getAccountAnalytics.request(data),
      getAccountAnalyticsChartData: data => getAccountAnalyticsChartData.request(data),
      getUserAnalytics: data => getUserAnalytics.request(data),
      getUserAnalyticsChartData: data => getUserAnalyticsChartData.request(data),
      getAccountEquity: data => getAccountEquity.request(data),
      getUserEquity: data => getUserEquity.request(data),
    }
  )
)(ClientAnalyticsPage);
export { ClientAnalyticsPage };
